import { ChevronRight, Inbox } from "lucide-react";
import type { DashboardRankingRowModel } from "./DashboardRankingDrawer";
import { DashboardSection } from "./DashboardSection";
import "./DashboardLedger.css";

const PREVIEW_COUNT = 3;

export interface DashboardRankingPreviewProps<
  Item extends DashboardRankingRowModel = DashboardRankingRowModel,
> {
  title: string;
  items: readonly Item[];
  emptyLabel?: string;
  openAllAriaLabel?: string;
  testIdForItem?: (item: Item) => string;
  onOpenAll: () => void;
  onOpenItem: (item: Item) => void;
}

const safeTestId = (value: string) => value.replace(/[^a-zA-Z0-9_-]+/g, "-");

const rowAccessibleName = (item: DashboardRankingRowModel) =>
  `Abrir detalhes de ${item.name}${item.metrics.length ? `; ${item.metrics.map((metric) => metric.accessibleLabel ?? `${metric.label}: ${metric.value}`).join("; ")}` : ""}`;

export function DashboardRankingPreview<
  Item extends DashboardRankingRowModel = DashboardRankingRowModel,
>({
  title,
  items,
  emptyLabel = "Nenhum registro neste período.",
  openAllAriaLabel,
  testIdForItem,
  onOpenAll,
  onOpenItem,
}: DashboardRankingPreviewProps<Item>) {
  return (
    <DashboardSection
      title={title}
      action={
        <button
          type="button"
          className="dashboard-ledger-action"
          {...(openAllAriaLabel ? { "aria-label": openAllAriaLabel } : {})}
          onClick={onOpenAll}
        >
          Ver todos
        </button>
      }
    >
      {items.length === 0 ? (
        <div className="dashboard-ledger-ranking__empty" role="status">
          <Inbox aria-hidden="true" size={19} />
          {emptyLabel}
        </div>
      ) : (
        <div className="dashboard-ledger-ranking__preview">
          {items.slice(0, PREVIEW_COUNT).map((item, index) => (
            <button
              key={item.id}
              type="button"
              className="dashboard-ledger-ranking__preview-row"
              aria-label={rowAccessibleName(item)}
              data-testid={
                testIdForItem?.(item) ??
                `dashboard-ranking-preview-${safeTestId(item.id)}`
              }
              onClick={() => onOpenItem(item)}
            >
              <span className="dashboard-ledger-ranking__position">
                {index + 1}
              </span>
              <span
                className="dashboard-ledger-ranking__name"
                title={item.name}
                style={{
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {item.name}
              </span>
              <span className="dashboard-ledger-ranking__metrics">
                {item.metrics.map((metric, metricIndex) => (
                  <span
                    key={`${metric.label}-${metricIndex}`}
                    className="dashboard-ledger-ranking__metric"
                  >
                    <span className="dashboard-ledger-ranking__metric-label">
                      {metric.label}
                    </span>
                    <span
                      className="dashboard-ledger-ranking__metric-value"
                      title={metric.accessibleLabel ?? metric.value}
                    >
                      {metric.value}
                    </span>
                  </span>
                ))}
              </span>
              <ChevronRight aria-hidden="true" size={17} />
            </button>
          ))}
        </div>
      )}
    </DashboardSection>
  );
}
